import { useEffect, useState } from "react";
import { FaReceipt } from "react-icons/fa";
import { toast } from "react-toastify";
import Pagination from "../../components/Pagination";
import { formatCurrency } from "../../help/Utils";
import {
  getPaymentTransactionDisplayId,
  getUserPayments,
  type Payment,
  type PaymentMetadata,
} from "../../API/paymentAPI";
import { formatDateMMDDYYYY } from "../../utils/dateFormatting";

interface PaymentTableRow {
  id: number;
  caseId: string;
  transactionId: string;
  date: string;
  serviceTitle: string;
  serviceDescription: string;
  package: string;
  packageColor: string;
  packageBgColor: string;
  platformFee: string;
  /** Formatted currency, or em dash when this checkout did not include USCIS. */
  uscisFee: string;
  status: string;
  statusColor: string;
  statusBorderColor: string;
  action: string;
}

/** Short label for the package chip (e.g. "Premium Package" → "Premium"). */
function packageDisplayLabel(name: string): string {
  const n = name.toLowerCase();
  if (n.includes("premium")) return "Premium";
  if (n.includes("basic")) return "Basic";
  return name;
}

function uscisFeeAmountFromMetadata(
  metadata: PaymentMetadata | null | undefined
): number | null {
  if (!metadata || typeof metadata !== "object") return null;
  let block = metadata.uscisFee;
  if (block == null) return null;
  if (typeof block === "string") {
    try {
      block = JSON.parse(block) as { amount?: number | string };
    } catch {
      return null;
    }
  }
  if (typeof block !== "object" || block == null || !("amount" in block)) {
    return null;
  }
  const n = Number((block as { amount?: number | string }).amount);
  return Number.isFinite(n) && n > 0 ? n : null;
}

const PaymentHistoryTable: React.FC = () => {
  const [payments, setPayments] = useState<PaymentTableRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 5;

  // Fetch payments on component mount
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        setError(null);
        const data: Payment[] = await getUserPayments();

        // Transform API data to table format
        const transformedData: PaymentTableRow[] = data.map((payment) => {
          // Format date
          const formattedDate = formatDateMMDDYYYY(payment.createdAt);

          const pkg = payment.package;
          const pkgName = pkg?.name ?? "";
          const pkgDesc = pkg?.description ?? "";

          // Determine package colors
          let packageColor = "#B25B00";
          let packageBgColor = "#FFDABD";

          if (pkgName.toLowerCase().includes("premium")) {
            packageColor = "#1F7A1F";
            packageBgColor = "#D9EACE";
          } else           if (pkgName.toLowerCase().includes("basic")) {
            packageColor = "#B25B00";
            packageBgColor = "#FFDABD";
          }

          let statusColor = "#D58800";
          let statusBorderColor = "#D58800";
          if (
            payment.status === "completed" ||
            payment.status === "succeeded"
          ) {
            statusColor = "#348400";
            statusBorderColor = "#348400";
          } else if (
            payment.status === "failed" ||
            payment.status === "canceled"
          ) {
            statusColor = "#B52222";
            statusBorderColor = "#B52222";
          }

          // Prefer application visa type from API; else infer from package text (e.g. "N-400")
          const fromApp = payment.application?.visaType?.trim();
          const visaTypeMatch = (pkgName || pkgDesc).match(/[NI]-\d{3,4}/i);
          const visaType =
            fromApp ||
            (visaTypeMatch ? visaTypeMatch[0].toUpperCase() : "") ||
            "";

          const lgvId = `LGV${String(payment.applicationId).padStart(2, "0")}`;
          const caseId = visaType ? `${lgvId}/${visaType}` : lgvId;

          const rawDetail = (pkgDesc || pkgName).trim();
          const onlyPackageLabel = /^(premium|basic)(\s+package)?$/i.test(
            rawDetail
          );
          const serviceDescription =
            visaType && (onlyPackageLabel || !rawDetail) ? "" : rawDetail;

          return {
            id: payment.id,
            caseId,
            transactionId: getPaymentTransactionDisplayId(payment),
            date: formattedDate,
            serviceTitle: visaType,
            serviceDescription,
            package: packageDisplayLabel(pkgName),
            packageColor,
            packageBgColor,
            platformFee: String(payment.package.price || "0.00"),
            uscisFee: (() => {
              const amt = uscisFeeAmountFromMetadata(payment.metadata ?? null);
              return amt != null ? formatCurrency(String(amt)) : "—";
            })(),
            status:
              payment.status.charAt(0).toUpperCase() + payment.status.slice(1),
            statusColor,
            statusBorderColor,
            action: "Receipt",
          };
        });

        setPayments(transformedData);
      } catch (err: any) {
        console.error("Error fetching payments:", err);
        setError(err.message || "Failed to load payment history");
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  // Calculate pagination
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedData = payments.slice(startIndex, endIndex);

  // Handle page change
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle receipt download
  const handleReceiptDownload = (payment: PaymentTableRow) => {
    // TODO: Implement receipt download functionality
    console.log("Download receipt for payment:", payment.id);
    toast.info("Receipt download is coming soon.");
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center py-20 mt-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1B365D]" />
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 mt-8">
        <p className="text-red-600 text-center">
          <strong>Error:</strong> {error}
        </p>
      </div>
    );
  }

  // Empty state
  if (payments.length === 0) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-12 mt-8 text-center">
        <FaReceipt className="text-gray-400 text-5xl mx-auto mb-4" />
        <h3 className="text-xl font-semibold text-gray-700 mb-2">
          No Payment History
        </h3>
        <p className="text-gray-500">
          You haven&apos;t made any payments yet. Your payment history will appear here once
          you complete a transaction.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="overflow-x-auto mt-8">
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-100 text-gray-700 text-sm leading-normal">
            <tr>
              <th
                scope="col"
                className="py-3 px-6 text-left uppercase text-[#374151] text-[14px] font-normal"
              >
                Case ID
              </th>
              <th
                scope="col"
                className="py-3 px-6 text-left uppercase text-[#374151] text-[14px] font-normal"
              >
                Transaction #
              </th>
              <th
                scope="col"
                className="py-3 px-6 text-left uppercase text-[#374151] text-[14px] font-normal"
              >
                Date
              </th>
              <th
                scope="col"
                className="py-3 px-6 text-left uppercase text-[#374151] text-[14px] font-normal"
              >
                Service
              </th>
              <th
                scope="col"
                className="py-3 px-6 text-left uppercase text-[#374151] text-[14px] font-normal"
              >
                Package
              </th>
              <th
                scope="col"
                className="py-3 px-6 text-left uppercase text-[#374151] text-[14px] font-normal"
              >
                Platform Fee
              </th>
              <th
                scope="col"
                className="py-3 px-6 text-left uppercase text-[#374151] text-[14px] font-normal"
              >
                USCIS Fee
              </th>
              <th
                scope="col"
                className="py-3 px-6 text-left uppercase text-[#374151] text-[14px] font-normal"
              >
                Status
              </th>
              <th
                scope="col"
                className="py-3 px-6 text-left uppercase text-[#374151] text-[14px] font-normal"
              >
                Actions
              </th>
            </tr>
          </thead>

          <tbody className="text-gray-600 text-sm divide-y divide-gray-200">
            {paginatedData.map((payment, index) => (
              <tr
                key={payment.id}
                className={`hover:bg-gray-100 ${
                  index % 2 === 0 ? "bg-white" : "bg-gray-50"
                }`}
              >
                <td className="py-3 px-6 text-[#1F2937]">
                  <strong>{payment.caseId}</strong>
                </td>
                <td className="py-3 px-6 max-w-[200px]">
                  <span
                    className="block truncate font-mono text-[11px] text-gray-600"
                    title={payment.transactionId}
                  >
                    {payment.transactionId || "—"}
                  </span>
                </td>
                <td className="py-3 px-6">{payment.date}</td>
                <td className="py-3 px-6">
                  {payment.serviceTitle ? (
                    <>
                      <strong>{payment.serviceTitle}</strong>
                      {payment.serviceDescription ? (
                        <>
                          <br />
                          {payment.serviceDescription}
                        </>
                      ) : null}
                    </>
                  ) : (
                    payment.serviceDescription
                  )}
                </td>
                <td className="py-3 px-6">
                  <span
                    className="inline-block max-w-[11rem] px-3 py-1.5 rounded-2xl text-sm font-medium leading-snug text-center break-words"
                    style={{
                      backgroundColor: payment.packageBgColor,
                      color: payment.packageColor,
                    }}
                  >
                    {payment.package}
                  </span>
                </td>
                <td className="py-3 px-6 text-[#1F2937]">
                  {payment.platformFee === "N.A"
                    ? "N/A"
                    : formatCurrency(payment.platformFee)}
                </td>
                <td className="py-3 px-6 text-[#1F2937]">{payment.uscisFee}</td>
                <td className="py-3 px-6">
                  <span
                    className="text-sm px-4 py-1 rounded-full inline-flex items-center border"
                    style={{
                      borderColor: payment.statusBorderColor,
                      color: payment.statusColor,
                    }}
                  >
                    {payment.status}
                  </span>
                </td>
                <td className="py-3 px-6 text-[#1B365D] font-bold">
                  {(payment.status === "Completed" ||
                    payment.status === "Succeeded") && (
                    <button
                      type="button"
                      onClick={() => handleReceiptDownload(payment)}
                      className="flex items-center hover:underline"
                    >
                      <FaReceipt className="me-2" /> {payment.action}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination
        totalItems={payments.length}
        itemsPerPage={itemsPerPage}
        onPageChange={handlePageChange}
      />
    </>
  );
};

export default PaymentHistoryTable;
